import { createFileRoute, notFound } from "@tanstack/react-router";
import { PrimeServicePage } from "@/components/site/PrimeServicePage";
import { getPrimeService } from "@/content/prime-services";
import { aeoGeo } from "@/lib/seo/schema";

export const Route = createFileRoute("/prime_/$slug")({
  loader: ({ params }) => {
    const service = getPrimeService(params.slug);
    if (!service) throw notFound();
    return { service };
  },
  head: ({ loaderData }) => {
    const s = loaderData?.service;
    if (!s) return { meta: [{ title: "Prime — Fenny" }] };
    const url = `https://fenny.se/prime/${s.slug}`;

    const serviceJsonLd = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: s.title,
      serviceType: s.shortTitle,
      description: s.metaDescription,
      provider: { "@type": "Organization", name: "Fenny" },
      areaServed: "SE",
      url,
    };

    const faqJsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: s.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };

    const breadcrumbJsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Hem", item: "/" },
        { "@type": "ListItem", position: 2, name: "Prime", item: "/prime" },
        { "@type": "ListItem", position: 3, name: s.shortTitle, item: url },
      ],
    };

    return {
      meta: [
        { title: s.metaTitle },
        { name: "description", content: s.metaDescription },
        { property: "og:title", content: s.metaTitle },
        { property: "og:description", content: s.metaDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: s.metaTitle },
        { name: "twitter:description", content: s.metaDescription },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: `/prime/${s.slug}` })) },
        { type: "application/ld+json", children: JSON.stringify(serviceJsonLd) },
        { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-wide pt-40 pb-32">
      <h1 className="text-4xl font-semibold">Tjänsten finns inte</h1>
      <p className="mt-4 text-muted-foreground">
        Sidan du letar efter finns inte. Se alla Prime-tjänster på{" "}
        <a href="/prime" className="text-mint underline">/prime</a>.
      </p>
    </div>
  ),
  component: ServiceRoute,
});

function ServiceRoute() {
  const { service } = Route.useLoaderData();
  return <PrimeServicePage service={service} />;
}
